import { AxisLabelsFormatterContextObject, Options, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { Currency, DemeterDataFrequency, DemeterUserType, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import formattingService from '../../../Services/Formatting/FormattingService';
import {
    areaRangeChartType,
    chartColors,
    ChartContext,
    defalutLineWidth,
    defaultChartAxisTickLabelStyle,
    defaultChartAxisTitleStyle,
    defaultChartOptions,
    defaultZoneAxis,
    featuredLineWidth,
    IChartAreaRangeDataSeries,
    IChartDataSeries,
    IChartProps,
    lineChartType,
} from '../ChartDefinitions';
import chartService from '../ChartService';
import { PlotBand } from '../MarketIndicator/MarketIndicatorChartRaw';
import { IMarketIndicatorChartColorPalette } from './MarketIndicatorUserChartDefinitions';

export interface IMarketIndicatorUserChartRawProps extends IChartProps {
    lineSeries: IChartDataSeries[];
    areaRangeSeries?: IChartAreaRangeDataSeries[];
    plotBands?: PlotBand[];
    currencies?: Currency[];
    unitOfMeasures?: UnitOfMeasure[];
    displayUnitOfMeasure?: UnitOfMeasure;
    displayWeekNumberTooltips?: boolean;
    focusOnLine?: boolean;
    hideAreaRangeSeriesInLegend?: boolean;
    colorPalette?: IMarketIndicatorChartColorPalette;
    startOnTick?: boolean;
    minimumAxisDate?: number;
    maximumAxisDate?: number;
    showMarkers?: boolean;
    disableTooltip?: boolean;
}

const lineSeriesBufferPercent = 5;
const dayInMilliseconds = 24 * 3600 * 1000;

const MarketIndicatorUserChartRaw: React.FC<IMarketIndicatorUserChartRawProps> = (props: IMarketIndicatorUserChartRawProps) => {
    // Application hooks.
    const currentUserType = useApplicationSelector(selectUserType);

    // Chart hooks.
    const marketsChartDefaultOptions = useMemo<Options>(
        () => ({
            ...defaultChartOptions,
            xAxis: {
                type: 'datetime',
                labels: {
                    formatter() {
                        const context = this as unknown as { value: number };
                        return formattingService.toMonthFromUtc(context.value);
                    },
                    style: defaultChartAxisTickLabelStyle,
                },
                tickInterval: 30 * dayInMilliseconds,
                startOnTick: props.startOnTick,
                min: props.minimumAxisDate,
                max: props.maximumAxisDate,
            },
            yAxis: [
                {
                    title: {
                        text: '',
                        style: defaultChartAxisTitleStyle,
                    },
                    labels: {
                        formatter() {
                            const context = this as unknown as AxisLabelsFormatterContextObject;
                            if (props.displayUnitOfMeasure === UnitOfMeasure.Percent) {
                                return formattingService.formatNumber(context.value, { type: 'percent' });
                            }

                            return formattingService.formatNumber(context.value, { precision: 1 }) ?? '0';
                        },
                        style: defaultChartAxisTickLabelStyle,
                    },
                    maxPadding: lineSeriesBufferPercent / 100.0,
                    minPadding: lineSeriesBufferPercent / 100.0,
                },
            ],
            series: [],
        }),
        [props.maximumAxisDate, props.displayUnitOfMeasure, props.lineSeries],
    );

    const [highchartOptions, setHighchartOptions] = useState<Options>(marketsChartDefaultOptions);

    const getDataSeriesDefinitions = useCallback(() => {
        const areasDataSeries = props.areaRangeSeries?.map((areaSeries, index) => {
            const currentColor =
                props.colorPalette?.dataDecileAlphaColorsRuleSetTwo && props.colorPalette.dataDecileAlphaColorsRuleSetTwo[index]
                    ? props.colorPalette.dataDecileAlphaColorsRuleSetTwo[index]
                    : chartColors.dataDecileAlphaColorsRuleSetTwo.find((x) => x.decileRank === areaSeries.decileRank)?.color ??
                      chartColors.areaChartColorsRuleSetTwo[index] ??
                      scssVariables.black;

            return {
                ...chartService.getDataSeriesBase(areaSeries, areaRangeChartType, currentColor),
                yAxis: 0,
                showInLegend: !props.hideAreaRangeSeriesInLegend,
                data: areaSeries.data.map((item) => [item.asOfDate.getTime(), item.minimumValue, item.maximumValue]),
                tooltip: {
                    enabled:
                        (currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner) &&
                        props.disableTooltip !== true,
                    customTooltipPerSeries() {
                        const context = this as unknown as ChartContext;

                        if (props.displayUnitOfMeasure === UnitOfMeasure.Percent) {
                            const modifiedContext = {
                                ...context,
                                point: {
                                    ...context.point,
                                    low: context.point.low ?? 0,
                                    high: context.point.high ?? 0,
                                },
                            };

                            return chartService.getAreaRangeTooltipText(modifiedContext, { format: 'percent' });
                        }

                        return chartService.getAreaRangeTooltipText(context);
                    },
                },
                zIndex: 1,
            };
        });

        const linesDataSeries = props.lineSeries.flatMap((lineSeries, index) => {
            const currentColor =
                props.colorPalette && props.colorPalette.lineChartColorsRuleSetTwo[index]
                    ? props.colorPalette.lineChartColorsRuleSetTwo[index]
                    : chartColors.lineChartColorsRuleSetTwo[index] ?? scssVariables.black;
            const primaryLine = index === 0 || lineSeries.isPrimaryLine;
            return [
                {
                    ...chartService.getDataSeriesBase(lineSeries, lineChartType, lineSeries.color ?? currentColor),
                    yAxis: lineSeries.yAxis ?? 0,
                    events: {
                        legendItemClick() {},
                    },
                    zoneAxis: defaultZoneAxis,
                    turboThreshold: 0,
                    data: lineSeries.data.map((item) => ({
                        x: item.asOfDate.getTime(),
                        y: item.value,
                        asOfDate: item.asOfDate,
                        originalAsOfDate: item.originalAsOfDate,
                        isActualValue: item.isActualValue,
                    })),
                    marker: {
                        enabled: props.showMarkers ?? false,
                    },
                    tooltip: {
                        enabled:
                            (currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner) &&
                            props.disableTooltip !== true,
                        customTooltipPerSeries() {
                            const context = this as unknown as ChartContext;
                            const minimumValue = Math.min(...props.lineSeries[0].data.map((x) => Math.abs(x.value)).filter((x) => !!x));
                            const numberOfDecimalPlaces = formattingService.getDisplayDecimalPlacesMinimumForCharts(minimumValue ?? 0);

                            if (props.displayUnitOfMeasure === UnitOfMeasure.Percent) {
                                const modifiedContext = {
                                    ...context,
                                    y: context.y,
                                };

                                return chartService.getTooltipText(modifiedContext, {
                                    dataFrequency: DemeterDataFrequency.Weekly,
                                    displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? numberOfDecimalPlaces,
                                    displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? numberOfDecimalPlaces,
                                    format: 'percent',
                                });
                            }

                            let format = '';
                            if (props.displayWeekNumberTooltips) {
                                format = 'weekNumber';
                            }

                            return chartService.getTooltipText(context, {
                                dataFrequency: DemeterDataFrequency.Weekly,
                                displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? numberOfDecimalPlaces,
                                displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? numberOfDecimalPlaces,
                                format,
                            });
                        },
                    },
                    zIndex: 2,
                    lineWidth: primaryLine ? featuredLineWidth : defalutLineWidth,
                },
            ];
        });

        return [...linesDataSeries, ...(areasDataSeries ?? [])];
    }, [props.areaRangeSeries, props.lineSeries, props.colorPalette, props.displayUnitOfMeasure, props.disableTooltip]);

    // Main useEffect to update chart when props or data changes.
    useEffect(() => {
        if (props.lineSeries.length === 0) {
            return;
        }

        const dataSeries = getDataSeriesDefinitions();
        const minimumValue = Math.min(...props.lineSeries[0].data.map((x) => Math.abs(x.value)).filter((x) => !!x));
        const numberOfDecimalPlaces = formattingService.getDisplayDecimalPlacesMinimumForCharts(minimumValue ?? 0);
        const newOptions = {
            ...marketsChartDefaultOptions,
            ...{ series: dataSeries },
            ...{
                xAxis: {
                    ...marketsChartDefaultOptions.xAxis,
                    plotBands: props.plotBands?.map((x) => ({
                        from: x.fromDate,
                        to: x.toDate,
                        color: x.color,
                    })),
                    scrollbar: {
                        enabled: false,
                    },
                    style: defaultChartAxisTickLabelStyle,
                },
            },
            ...{ yAxis: [...(marketsChartDefaultOptions.yAxis as YAxisOptions[])!] },

            tooltip: {
                // Highcharts only reads enabled from the chart level tooltip, so the per series flag alone never hides it.
                enabled: props.disableTooltip !== true,
                formatter() {
                    return (this as unknown as ChartContext).series.tooltipOptions.customTooltipPerSeries.call(this);
                },
            },
            exporting: {
                chartOptions: {
                    series: dataSeries.map((x) => {
                        if (x.type === 'arearange') {
                            return { ...x, color: x.color, opacity: 0.7 };
                        }
                        return x;
                    }),
                    xAxis: {
                        ...marketsChartDefaultOptions.xAxis,
                        plotBands: props.plotBands?.map((x) => ({
                            from: x.fromDate,
                            to: x.toDate,
                            color: x.color,
                            opacity: 0.7,
                        })),
                        scrollbar: {
                            enabled: false,
                        },
                    },
                },
            },
        };

        const lineMinimumValue = getMinimumValue([...props.lineSeries.map((x) => x.data.map((y) => y.value)).flat()]);

        const lineMaximumValue = getMaximumValue([...props.lineSeries.map((x) => x.data.map((y) => y.value)).flat()]);

        (newOptions.yAxis as YAxisOptions[])[0].min = props.focusOnLine ? lineMinimumValue : undefined;
        (newOptions.yAxis as YAxisOptions[])[0].max = props.focusOnLine ? lineMaximumValue : undefined;

        const maximumYAxis = Math.max(...props.lineSeries.map((x) => (x.yAxis ?? 0) as number));
        if (maximumYAxis > 0) {
            for (let index = 0; index < maximumYAxis; index += 1) {
                const currentYAxis = {
                    title: {
                        text: '',
                        style: defaultChartAxisTitleStyle,
                    },
                    labels: {
                        formatter: function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                            const context = this as unknown as AxisLabelsFormatterContextObject;
                            if (props.displayUnitOfMeasure === UnitOfMeasure.Percent) {
                                return formattingService.formatNumber(context.value, { type: 'percent' });
                            }

                            return formattingService.formatNumber(context.value, { precision: numberOfDecimalPlaces });
                        },
                        style: defaultChartAxisTickLabelStyle,
                    },
                    opposite: index % 2 === 0,
                };

                newOptions.yAxis.push({ ...(marketsChartDefaultOptions.yAxis as YAxisOptions[])!, ...currentYAxis });
            }
        }

        if (props.areaRangeSeries) {
            newOptions.chart = {
                type: areaRangeChartType,
            };
        }

        let unitOfMeasures = props.unitOfMeasures ?? [];
        const displayUnitOfMeasure = props.displayUnitOfMeasure && (props.displayUnitOfMeasure as string) !== '' ? props.displayUnitOfMeasure : undefined;
        if (displayUnitOfMeasure) {
            unitOfMeasures = [displayUnitOfMeasure];
        }

        unitOfMeasures.forEach((x, index) => {
            const prefix = props.unitOfMeasures?.length && props.unitOfMeasures?.length > 1 ? `${dataSeries[index].name} - ` : '';

            (newOptions.yAxis as YAxisOptions[])[index].title!.text =
                props.lineSeries[index].axisLabel ?? `${prefix}${chartService.getCurrencyAndUnitOfMeasureText(x, props.currencies![index])}`;

            (newOptions.yAxis as YAxisOptions[])[index].labels!.formatter = function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                const context = this as unknown as AxisLabelsFormatterContextObject;
                if (props.displayUnitOfMeasure === UnitOfMeasure.Percent) {
                    return formattingService.formatNumber(context.value, { type: 'percent' });
                }
                return formattingService.formatNumber(context.value, { precision: numberOfDecimalPlaces });
            };
        });

        setHighchartOptions(newOptions as Options);
    }, [
        props.lineSeries,
        props.areaRangeSeries,
        props.plotBands,
        props.maximumAxisDate,
        props.colorPalette,
        props.unitOfMeasures,
        props.displayUnitOfMeasure,
        props.currencies,
        props.displayDecimalPlacesMinimum,
        props.displayDecimalPlacesMaximum,
        // The restricted data source arrives after the series, so the options have to be rebuilt when the tooltip gate flips.
        props.disableTooltip,
    ]);

    const getMinimumValue = (initialArray: number[]) => {
        let arrayLength = initialArray.length;
        let minimumValue = Infinity;

        while (arrayLength) {
            if (Number(initialArray[arrayLength]) < minimumValue) {
                minimumValue = Number(initialArray[arrayLength]);
            }

            arrayLength -= 1;
        }

        return minimumValue;
    };

    const getMaximumValue = (initialArray: number[]) => {
        let arrayLength = initialArray.length;
        let maximumValue = -Infinity;

        while (arrayLength) {
            if (Number(initialArray[arrayLength]) > maximumValue) {
                maximumValue = Number(initialArray[arrayLength]);
            }

            arrayLength -= 1;
        }

        return maximumValue;
    };

    return <HighchartsReact ref={props.chartReference} highcharts={HighStock} options={highchartOptions} containerProps={{ style: { height: '100%' } }} />;
};

export default memo(MarketIndicatorUserChartRaw);
